import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { endPoints } from '../../Config';
import PhotoEvidenceUpload from '../../components/PhotoEvidenceUpload';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { translateUiText, translateUiToken } from '../../utilities/siteTranslations';

const DEFAULT_REPAIR_TYPES = [
    'ajuste de bandeja',
    'ajuste de espirales',
    'billeteros',
    'cables de comunicación',
    'cables de corriente',
    'compresores',
    'fuentes de poder',
    'grupo café',
    'molinos',
    'monederos',
    'motor de grupo de café',
    'parlevel',
    'pintar máquina',
    'POS',
    'puertas',
    'relay sólido',
    'rotulado especial',
    'sensores de caída',
    'tarjetas CPU',
    'tarjetas de potencia',
    'otro'
];

const PRIORITY_OPTIONS = [
    { label: 'Normal', value: 'normal' },
    { label: 'Alta', value: 'high' },
    { label: 'Urgente', value: 'urgent' },
    { label: 'Baja', value: 'low' }
];

const emptyTaskForm = {
    repairType: '',
    customRepairType: '',
    sparePartId: '',
    technicianId: '',
    priority: 'normal',
    plannedNotes: ''
};

const emptyEvidenceForm = {
    photos: [],
    completionComment: '',
    completionResult: '',
    sparePartId: '',
    inventoryQuantity: 1
};

const COMPLETION_RESULT_OPTIONS = [
    { label: 'Repaired and ready to use', value: 'ready_for_inventory' },
    { label: 'Pending testing', value: 'pending_test' },
    { label: 'Not repairable', value: 'not_repairable' }
];

const getUserName = (user) => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '-';

const getPartName = (part) => {
    if (!part) return '-';
    return `${part.sku || ''} - ${part.description || ''}`.trim() || '-';
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');

const formatDuration = (secondsValue) => {
    const total = Math.max(0, Number(secondsValue || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getLiveElapsedSeconds = (task, nowTick) => {
    if (task?.status === 'in_progress' && task?.timerStartedAt) {
        const stored = Number(task?.totalActiveSeconds || 0);
        return stored + Math.max(0, Math.floor((nowTick - new Date(task.timerStartedAt).getTime()) / 1000));
    }
    return Number(task?.elapsedSeconds ?? task?.totalActiveSeconds ?? 0);
};

const getTaskRows = (tasks, status) => tasks.filter((task) => task.status === status);

const SparePartRepairTasks = ({ partsInventory = [], preferredLanguage, onInventoryChanged }) => {
    const dispatch = useDispatch();
    const [repairTasks, setRepairTasks] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [repairTypes, setRepairTypes] = useState(DEFAULT_REPAIR_TYPES);
    const [taskDialogVisible, setTaskDialogVisible] = useState(false);
    const [evidenceDialogVisible, setEvidenceDialogVisible] = useState(false);
    const [completeOnSave, setCompleteOnSave] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskForm, setTaskForm] = useState(emptyTaskForm);
    const [evidenceForm, setEvidenceForm] = useState(emptyEvidenceForm);
    const [actionLoadingIds, setActionLoadingIds] = useState(() => new Set());
    const [nowTick, setNowTick] = useState(Date.now());

    const currentUser = JSON.parse(localStorage.getItem('userDetails') || localStorage.getItem('adminUser') || '{}');
    const currentAdmin = currentUser?.data?.adminUser || {};
    const t = useCallback((value) => translateUiText(value, preferredLanguage), [preferredLanguage]);

    const technicianOptions = useMemo(
        () => technicians.map((user) => ({ label: getUserName(user), value: user._id })),
        [technicians]
    );

    const repairTypeOptions = useMemo(
        () => repairTypes.map((type) => ({ label: type === 'otro' ? t('Other') : type, value: type })),
        [repairTypes, t]
    );

    const completionResultOptions = useMemo(
        () => COMPLETION_RESULT_OPTIONS.map((option) => ({ ...option, label: t(option.label) })),
        [t]
    );

    const sparePartOptions = useMemo(
        () =>
            partsInventory
                .filter((part) => part?.isActive !== false)
                .slice()
                .sort((a, b) => String(a.description || '').localeCompare(String(b.description || '')))
                .map((part) => ({
                    label: `${getPartName(part)} · ${t(part.condition || '-')} · ${part.location || '-'}`,
                    value: part._id
                })),
        [partsInventory, t]
    );

    const selectedReturnPart = useMemo(
        () => partsInventory.find((part) => part?._id === evidenceForm.sparePartId) || null,
        [evidenceForm.sparePartId, partsInventory]
    );

    const plannedTasks = useMemo(() => getTaskRows(repairTasks, 'planned'), [repairTasks]);
    const inProgressTasks = useMemo(() => getTaskRows(repairTasks, 'in_progress'), [repairTasks]);
    const completedTasks = useMemo(() => getTaskRows(repairTasks, 'completed'), [repairTasks]);

    const loadRepairTasks = useCallback(async () => {
        const response = await handleGetRequest(endPoints.getSparePartRepairTasks(), true, false, dispatch);
        setRepairTasks(Array.isArray(response?.data) ? response.data : []);
    }, [dispatch]);

    const loadTechnicians = useCallback(async () => {
        const response = await handleGetRequest(endPoints.getTechnicians, true, false, dispatch);
        setTechnicians(Array.isArray(response?.data) ? response.data : []);
    }, [dispatch]);

    const loadRepairTypes = useCallback(async () => {
        const response = await handleGetRequest(endPoints.getSparePartRepairTypes, false, false, dispatch);
        const nextTypes = response?.data?.repairTypes;
        if (Array.isArray(nextTypes) && nextTypes.length > 0) setRepairTypes(nextTypes);
    }, [dispatch]);

    const setTaskActionLoading = (actionId, isLoading) => {
        setActionLoadingIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (isLoading) nextIds.add(actionId);
            else nextIds.delete(actionId);
            return nextIds;
        });
    };

    const replaceRepairTask = (updatedTask) => {
        if (!updatedTask?._id) return;
        setRepairTasks((currentTasks) => {
            const taskExists = currentTasks.some((task) => task._id === updatedTask._id);
            if (!taskExists) return [...currentTasks, updatedTask];
            return currentTasks.map((task) => (task._id === updatedTask._id ? updatedTask : task));
        });
    };

    const runTaskAction = async (task, action, payload = {}) => {
        if (!task?._id) return;

        const endpointMap = {
            start: endPoints.startSparePartRepairTask(task._id),
            pause: endPoints.pauseSparePartRepairTask(task._id),
            resume: endPoints.resumeSparePartRepairTask(task._id),
            complete: endPoints.completeSparePartRepairTask(task._id),
            evidence: endPoints.updateSparePartRepairTaskEvidence(task._id)
        };

        const actionId = `${task._id}-${action}`;
        setTaskActionLoading(actionId, true);
        const body = { changedBy: currentAdmin?._id || '', ...payload };
        try {
            // Timer actions deliberately avoid the page-wide loader. Each repair
            // owns its request state so technicians can start or pause different
            // repairs without one action blocking the rest of the table.
            const response =
                action === 'evidence'
                    ? await dispatch(handlePatchRequest(body, endpointMap[action], false))
                    : await dispatch(handlePostRequest(body, endpointMap[action], false));

            if (response?.status >= 200 && response?.status < 300) {
                replaceRepairTask(response?.data?.data);
                if (action === 'complete' && typeof onInventoryChanged === 'function') {
                    await onInventoryChanged();
                }
                setEvidenceDialogVisible(false);
                setSelectedTask(null);
                setEvidenceForm(emptyEvidenceForm);
            }
        } finally {
            setTaskActionLoading(actionId, false);
        }
    };

    const handleCreateTask = async () => {
        if (!taskForm.repairType || !taskForm.technicianId) {
            toast.warn(t('Repair type and technician are required.'));
            return;
        }
        if (taskForm.repairType === 'otro' && !taskForm.customRepairType.trim()) {
            toast.warn(t('Custom repair type is required.'));
            return;
        }

        const payload = {
            ...taskForm,
            createdBy: currentAdmin?._id || '',
            sparePartId: taskForm.sparePartId || null
        };
        const response = await dispatch(handlePostRequest(payload, endPoints.createSparePartRepairTask, true));
        if (response?.status === 201) {
            setTaskDialogVisible(false);
            setTaskForm(emptyTaskForm);
            await loadRepairTasks();
        }
    };

    const handleAssignTechnician = async (task, technicianId) => {
        const response = await dispatch(
            handlePatchRequest(
                { technicianId, changedBy: currentAdmin?._id || '' },
                endPoints.assignSparePartRepairTask(task._id),
                true
            )
        );
        if (response?.status === 200) await loadRepairTasks();
    };

    const openEvidenceDialog = (task, shouldComplete = false) => {
        setSelectedTask(task);
        setCompleteOnSave(shouldComplete);
        setEvidenceForm({
            photos: Array.isArray(task?.photos) ? task.photos : [],
            completionComment: task?.completionComment || '',
            completionResult: task?.completionResult || '',
            sparePartId: task?.sparePart?._id || task?.sparePart || '',
            inventoryQuantity: Number(task?.inventoryQuantity || 1)
        });
        setEvidenceDialogVisible(true);
    };

    const saveEvidence = async (addToInventory = false) => {
        if (!selectedTask) return;
        const payload = {
            photos: evidenceForm.photos,
            completionComment: evidenceForm.completionComment
        };

        if (!completeOnSave) {
            await runTaskAction(selectedTask, 'evidence', payload);
            return;
        }

        if (!evidenceForm.completionResult) {
            toast.warn(t('Select the repair result.'));
            return;
        }

        if (addToInventory) {
            if (evidenceForm.completionResult !== 'ready_for_inventory') {
                toast.warn(t('Only a repaired part that is ready to use can be added to inventory.'));
                return;
            }
            if (!evidenceForm.sparePartId || Number(evidenceForm.inventoryQuantity || 0) < 1) {
                toast.warn(t('Part and quantity are required to add it to inventory.'));
                return;
            }
        }

        await runTaskAction(selectedTask, 'complete', {
            ...payload,
            completionResult: evidenceForm.completionResult,
            addToInventory,
            sparePartId: evidenceForm.sparePartId || null,
            inventoryQuantity: Number(evidenceForm.inventoryQuantity || 1)
        });
    };

    const repairTypeBody = (task) => task.displayRepairType || task.repairType || '-';
    const technicianBody = (task) => getUserName(task.technician);
    const partBody = (task) => getPartName(task.sparePart);
    const durationBody = (task) => formatDuration(getLiveElapsedSeconds(task, nowTick));
    const photoBody = (task) => (Array.isArray(task.photos) ? task.photos.length : 0);
    const completionResultBody = (task) => {
        const labels = {
            ready_for_inventory: t('Repaired and ready to use'),
            pending_test: t('Pending testing'),
            not_repairable: t('Not repairable')
        };
        return labels[task.completionResult] || '-';
    };
    const inventoryBody = (task) => {
        if (task.inventoryDisposition === 'added' && Number(task.inventoryQuantity || 0) > 0) {
            return (
                <div className="flex flex-column gap-1">
                    <span className="text-green-700 font-semibold">
                        {t('Added to inventory')}: +{task.inventoryQuantity}
                    </span>
                    <small className="text-600">{getPartName(task.inventorySparePart || task.sparePart)}</small>
                </div>
            );
        }
        return <span className="text-600">{t('Not added to inventory')}</span>;
    };

    const plannedActionsBody = (task) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-play"
                label={t('Start')}
                className="p-button-success p-button-sm"
                loading={actionLoadingIds.has(`${task._id}-start`)}
                onClick={() => runTaskAction(task, 'start')}
            />
        </div>
    );

    const plannedTechnicianBody = (task) => (
        <Dropdown
            value={task.technician?._id || task.technician}
            options={technicianOptions}
            onChange={(e) => handleAssignTechnician(task, e.value)}
            placeholder={t('Select technician')}
            className="w-full"
        />
    );

    const progressActionsBody = (task) => {
        const isRunning = Boolean(task.timerStartedAt);
        return (
            <div className="flex gap-2 flex-wrap">
                <Button
                    icon={isRunning ? 'pi pi-pause' : 'pi pi-play'}
                    label={isRunning ? t('Pause') : t('Continue')}
                    className={isRunning ? 'p-button-warning p-button-sm' : 'p-button-success p-button-sm'}
                    loading={actionLoadingIds.has(`${task._id}-${isRunning ? 'pause' : 'resume'}`)}
                    onClick={() => runTaskAction(task, isRunning ? 'pause' : 'resume')}
                />
                <Button
                    icon="pi pi-camera"
                    className="p-button-secondary p-button-sm"
                    tooltip={t('Photos')}
                    onClick={() => openEvidenceDialog(task)}
                />
                <Button
                    icon="pi pi-check"
                    label={t('Finish')}
                    className="p-button-primary p-button-sm"
                    loading={actionLoadingIds.has(`${task._id}-complete`)}
                    onClick={() => openEvidenceDialog(task, true)}
                />
            </div>
        );
    };

    useEffect(() => {
        loadRepairTasks();
        loadTechnicians();
        loadRepairTypes();
    }, [loadRepairTasks, loadTechnicians, loadRepairTypes]);

    useEffect(() => {
        const interval = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="mb-5">
            <Dialog
                header={t('New Spare Part Repair')}
                visible={taskDialogVisible}
                onHide={() => setTaskDialogVisible(false)}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '42vw' }}
                draggable={false}
            >
                <div className="grid p-fluid">
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Repair Type')}</label>
                        <Dropdown
                            value={taskForm.repairType}
                            options={repairTypeOptions}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, repairType: e.value }))}
                            placeholder={t('Select repair type')}
                            filter
                        />
                    </div>
                    {taskForm.repairType === 'otro' ? (
                        <div className="col-12 md:col-6">
                            <label className="block mb-2">{t('Other')}</label>
                            <InputText
                                value={taskForm.customRepairType}
                                onChange={(e) => setTaskForm((prev) => ({ ...prev, customRepairType: e.target.value }))}
                            />
                        </div>
                    ) : null}
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Technician')}</label>
                        <Dropdown
                            value={taskForm.technicianId}
                            options={technicianOptions}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, technicianId: e.value }))}
                            placeholder={t('Select technician')}
                            filter
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Part')}</label>
                        <Dropdown
                            value={taskForm.sparePartId}
                            options={sparePartOptions}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, sparePartId: e.value }))}
                            placeholder={t('Optional')}
                            showClear
                            filter
                        />
                    </div>
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{t('Priority')}</label>
                        <Dropdown
                            value={taskForm.priority}
                            options={PRIORITY_OPTIONS}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, priority: e.value }))}
                        />
                    </div>
                    <div className="col-12">
                        <label className="block mb-2">{t('Notes')}</label>
                        <InputTextarea
                            value={taskForm.plannedNotes}
                            onChange={(e) => setTaskForm((prev) => ({ ...prev, plannedNotes: e.target.value }))}
                            rows={3}
                            autoResize
                        />
                    </div>
                </div>
                <div className="flex justify-content-end gap-2 mt-3">
                    <Button label={t('Cancel')} className="p-button-text" onClick={() => setTaskDialogVisible(false)} />
                    <Button label={t('Save')} icon="pi pi-save" onClick={handleCreateTask} />
                </div>
            </Dialog>

            <Dialog
                header={completeOnSave ? t('Finish Repair Task') : t('Photos and Comments')}
                visible={evidenceDialogVisible}
                onHide={() => setEvidenceDialogVisible(false)}
                breakpoints={{ '960px': '80vw', '640px': '100vw' }}
                style={{ width: '58vw' }}
                draggable={false}
            >
                <div className="p-fluid">
                    <PhotoEvidenceUpload
                        label={t('Photos')}
                        value={evidenceForm.photos}
                        onChange={(photos) => setEvidenceForm((prev) => ({ ...prev, photos }))}
                        maxPhotos={10}
                    />
                    <div className="mt-3">
                        <label className="block mb-2">{t('Comments')}</label>
                        <InputTextarea
                            value={evidenceForm.completionComment}
                            onChange={(e) => setEvidenceForm((prev) => ({ ...prev, completionComment: e.target.value }))}
                            rows={4}
                            autoResize
                        />
                    </div>
                    {completeOnSave ? (
                        <>
                            <div className="mt-3">
                                <label className="block mb-2">
                                    {t('Repair result')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    value={evidenceForm.completionResult}
                                    options={completionResultOptions}
                                    onChange={(event) =>
                                        setEvidenceForm((prev) => ({
                                            ...prev,
                                            completionResult: event.value
                                        }))
                                    }
                                    placeholder={t('Select the repair result')}
                                    className="w-full"
                                />
                            </div>

                            {evidenceForm.completionResult === 'ready_for_inventory' ? (
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-8">
                                        <label className="block mb-2">
                                            {t('Part to return')} <span className="text-red-500">*</span>
                                        </label>
                                        <Dropdown
                                            value={evidenceForm.sparePartId}
                                            options={sparePartOptions}
                                            onChange={(event) =>
                                                setEvidenceForm((prev) => ({
                                                    ...prev,
                                                    sparePartId: event.value
                                                }))
                                            }
                                            placeholder={t('Select Spare Part')}
                                            className="w-full"
                                            filter
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label className="block mb-2">
                                            {t('Quantity to add')} <span className="text-red-500">*</span>
                                        </label>
                                        <InputNumber
                                            value={evidenceForm.inventoryQuantity}
                                            onValueChange={(event) =>
                                                setEvidenceForm((prev) => ({
                                                    ...prev,
                                                    inventoryQuantity: event.value || 1
                                                }))
                                            }
                                            min={1}
                                            max={999}
                                            showButtons
                                            className="w-full"
                                        />
                                    </div>
                                    {selectedReturnPart ? (
                                        <div className="col-12">
                                            <div className="surface-100 border-round p-3 text-700">
                                                {t('This action will add')} <strong>{evidenceForm.inventoryQuantity || 1} × {getPartName(selectedReturnPart)}</strong>{' '}
                                                {t('to available inventory as Ready to Install')}. {t('Inventory location')}:{' '}
                                                <strong>{selectedReturnPart.location || '-'}</strong>.
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
                <div className="flex justify-content-end gap-2 mt-3 flex-wrap">
                    <Button label={t('Cancel')} className="p-button-text" onClick={() => setEvidenceDialogVisible(false)} />
                    {completeOnSave ? (
                        <>
                            <Button
                                label={t('Finish without adding to inventory')}
                                icon="pi pi-check"
                                className="p-button-outlined p-button-secondary"
                                loading={actionLoadingIds.has(`${selectedTask?._id}-complete`)}
                                disabled={!evidenceForm.completionResult}
                                onClick={() => saveEvidence(false)}
                            />
                            <Button
                                label={t('Finish and add to available inventory')}
                                icon="pi pi-box"
                                className="p-button-success"
                                loading={actionLoadingIds.has(`${selectedTask?._id}-complete`)}
                                disabled={
                                    evidenceForm.completionResult !== 'ready_for_inventory' ||
                                    !evidenceForm.sparePartId ||
                                    Number(evidenceForm.inventoryQuantity || 0) < 1
                                }
                                onClick={() => saveEvidence(true)}
                            />
                        </>
                    ) : (
                        <Button label={t('Save')} icon="pi pi-save" onClick={() => saveEvidence(false)} />
                    )}
                </div>
            </Dialog>

            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="m-0">{t('Spare Part Repairs')}</h4>
                    <small className="text-600">{t('Repair workflow for reusable components with tracked work time.')}</small>
                </div>
                <Button label={t('New Repair')} icon="pi pi-plus" className="p-button-success p-button-sm" onClick={() => setTaskDialogVisible(true)} />
            </div>

            <div className="mb-4">
                <div className="flex justify-content-between align-items-center mb-2">
                    <h5 className="m-0">{t('Planned')}</h5>
                    <span className="text-sm text-600">{plannedTasks.length} {t('planned')}</span>
                </div>
                <DataTable value={plannedTasks} paginator rows={5} rowsPerPageOptions={[5, 10, 20]} emptyMessage={t('No planned repair tasks.')} responsiveLayout="scroll" className="p-datatable-sm">
                    <Column header={t('Created At')} body={(task) => formatDateTime(task.createdAt)} />
                    <Column header={t('Repair Type')} body={repairTypeBody} style={{ minWidth: '13rem' }} />
                    <Column header={t('Part')} body={partBody} style={{ minWidth: '16rem' }} />
                    <Column header={t('Technician')} body={plannedTechnicianBody} style={{ minWidth: '15rem' }} />
                    <Column header={t('Priority')} body={(task) => translateUiToken(task.priority || '-', preferredLanguage)} />
                    <Column header={t('Notes')} body={(task) => task.plannedNotes || '-'} style={{ minWidth: '14rem' }} />
                    <Column header={t('Actions')} body={plannedActionsBody} style={{ minWidth: '8rem' }} />
                </DataTable>
            </div>

            <div className="mb-4">
                <div className="flex justify-content-between align-items-center mb-2">
                    <h5 className="m-0">{t('In Progress')}</h5>
                    <span className="text-sm text-600">{inProgressTasks.length} {t('in progress')}</span>
                </div>
                <DataTable value={inProgressTasks} paginator rows={5} rowsPerPageOptions={[5, 10, 20]} emptyMessage={t('No repair tasks in progress.')} responsiveLayout="scroll" className="p-datatable-sm">
                    <Column header={t('Started At')} body={(task) => formatDateTime(task.startedAt)} />
                    <Column header={t('Repair Type')} body={repairTypeBody} style={{ minWidth: '13rem' }} />
                    <Column header={t('Part')} body={partBody} style={{ minWidth: '16rem' }} />
                    <Column header={t('Technician')} body={technicianBody} />
                    <Column header={t('Time')} body={durationBody} />
                    <Column header={t('Timer')} body={(task) => (task.timerStartedAt ? t('Running') : t('Paused'))} />
                    <Column header={t('Photos')} body={photoBody} />
                    <Column header={t('Actions')} body={progressActionsBody} style={{ minWidth: '18rem' }} />
                </DataTable>
            </div>

            <div className="mb-4">
                <div className="flex justify-content-between align-items-center mb-2">
                    <h5 className="m-0">{t('Completed')}</h5>
                    <span className="text-sm text-600">{completedTasks.length} {t('completed')}</span>
                </div>
                <DataTable value={completedTasks} paginator rows={10} rowsPerPageOptions={[5, 10, 20]} emptyMessage={t('No completed repair tasks.')} responsiveLayout="scroll" className="p-datatable-sm">
                    <Column header={t('Completed At')} body={(task) => formatDateTime(task.completedAt)} />
                    <Column header={t('Repair Type')} body={repairTypeBody} style={{ minWidth: '13rem' }} />
                    <Column header={t('Part')} body={partBody} style={{ minWidth: '16rem' }} />
                    <Column header={t('Technician')} body={technicianBody} />
                    <Column header={t('Total Time')} body={(task) => formatDuration(task.elapsedSeconds ?? task.totalActiveSeconds)} />
                    <Column header={t('Pauses')} body={(task) => (Array.isArray(task.pauseEvents) ? task.pauseEvents.length : 0)} />
                    <Column header={t('Photos')} body={photoBody} />
                    <Column header={t('Repair result')} body={completionResultBody} style={{ minWidth: '12rem' }} />
                    <Column header={t('Inventory result')} body={inventoryBody} style={{ minWidth: '14rem' }} />
                    <Column header={t('Comments')} body={(task) => task.completionComment || '-'} style={{ minWidth: '16rem' }} />
                    <Column
                        header={t('Actions')}
                        body={(task) => <Button icon="pi pi-eye" className="p-button-rounded p-button-text p-button-sm" tooltip={t('View')} onClick={() => openEvidenceDialog(task)} />}
                    />
                </DataTable>
            </div>
        </div>
    );
};

export default SparePartRepairTasks;
